"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 20: Family Computer Disk System (FDS)
 * ------------------------------------------------------------------ *
 * The FDS is not a bank-switching board: mapper 20 is the number iNES
 * reserves for the RAM adaptor (RP2C33), and a .fds image is disk sides, not
 * PRG banks. What a game needs from us is
 *
 *   $6000-$DFFF  32 KB of PRG RAM (the disk is loaded here by the BIOS)
 *   $E000-$FFFF   8 KB BIOS ROM (disksys.rom, supplied by nes.opts.fdsBios)
 *   $4020-$4025   timer IRQ and the disk drive control registers
 *   $4026/$4033   the expansion connector, where the BIOS reads the battery
 *                 "power good" bit from
 *
 * and, behind those write-only registers, a disk drive: the BIOS bit-bangs the
 * 2C33 and expects a serial byte stream back. This file models the drive as a
 * byte stream built from the block structure of the disk side, at the real
 * 96.4 kbit/s, with the "block start mark", the CRCs and the head wrapping at
 * the end of the disk. The register semantics follow
 * https://www.nesdev.org/wiki/Family_Computer_Disk_System and Brad Taylor's
 * "Famicom Disk System technical reference", which is also where the BIOS
 * sequences (WaitForRdy, CheckBlkType, EndOfBlkRead...) come from: this drive
 * is written to satisfy exactly those sequences.
 *
 * Known gap, deliberately left for later: the extra sound channel ($4040-$4098,
 * see FDS audio on nesdev). Everything else a game reads is here.
 */

var FDS_SIDE_SIZE = 65500; // fwNES side size, gaps and CRCs stripped
var FDS_BIOS_SIZE = 0x2000;
var FDS_RAM_SIZE = 0x8000; // 32 KB of PRG RAM at $6000-$DFFF

// 96.4 kbit/s at the Famicom's 1.7897725 MHz: 12050 bytes/s, i.e. 148.5 CPU
// cycles per byte. Emulators round to 149.
var FDS_BYTE_CYCLES = 149;

// Zeros written between the end of a block and the next block start mark (and
// before the first one). A real disk uses 976 bits (~122 bytes); the exact size
// does not matter here because the BIOS waits in absolute time (Delay), not for
// a bit count, but the gap has to exist: the adaptor only sees a block boundary
// when the mark bit comes along.
var FDS_GAP = 122;

// The head keeps reading zeros after the last block until it reaches the inner
// track and clicks back to the start.
var FDS_TRAILING_GAP = 4096;

var FDS_MARK = 0x80; // gap terminator + first bit of a block, little endian

// CRC-16/KERMIT computed the way the 2C33 does it (Brad Taylor's algorithm):
// the register starts at the block start mark, then every byte is shifted in
// LSB first against the reversed X25 polynomial. Two zero bytes appended to a
// block end up holding the CRC low, then high, and feeding those two bytes
// leaves the register at 0 -- which is exactly what $4030 bit 4 checks.
var FDS_CRC_POLY = 0x8408;
var FDS_CRC_INIT = 0x8000;

function crcFeed(crc, byte) {
  crc ^= byte & 0xff;
  for (var i = 0; i < 8; i++) {
    crc = crc & 1 ? (crc >>> 1) ^ FDS_CRC_POLY : crc >>> 1;
  }
  return crc & 0xffff;
}

function blockCrc(bytes) {
  var crc = FDS_CRC_INIT;
  for (var i = 0; i < bytes.length; i++) {
    crc = crcFeed(crc, bytes[i]);
  }
  return crc & 0xffff;
}

// Accepts anything a front end may hand us for the BIOS image.
function toByteArray(data) {
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }
  if (data && typeof data.length === "number") {
    return new Uint8Array(data);
  }
  return null;
}

function register(Mappers) {
  Mappers[20] = function (nes) {
    this.nes = nes;
    this.prgRam = new Uint8Array(FDS_RAM_SIZE);
    this.bios = null;
    this.sides = [];
    this.blocks = [];
    this.stream = new Uint8Array(0);
    this.blockMarks = [];
  };

  Mappers[20].prototype = new Mappers[0]();

  // The $4025 mirroring bit owns the name tables, and nes.reset() must not put
  // them back to the header's arrangement behind the mapper's back.
  Mappers[20].prototype.ownsMirroring = true;

  // The timer and the drive both run on CPU cycles; NES.frame() clocks us after
  // every CPU burst.
  Mappers[20].prototype.watchesCpuCycles = true;

  Mappers[20].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    // $4020/$4021: timer reload value (not gated by $4023).
    this.timerReload = 0;
    this.timerCounter = 0;
    this.timerEnabled = false;
    this.timerRepeat = false;
    this.timerIrq = false;

    // $4023: master I/O enable. The BIOS writes $83 during reset.
    this.diskIoEnabled = false;
    this.soundIoEnabled = false;

    // $4024/$4025/$4026.
    this.diskData = 0;
    this.diskControl = 0;
    this.extWrite = 0;

    this.motorOn = false;
    this.transferReset = true;
    this.readMode = false;
    this.crcControl = false;
    this.transferBehaviour = false;
    this.diskIrqEnabled = false;

    // The drive.
    this.byteReady = false;
    this.byteValue = 0;
    this.diskIrq = false;
    this.crcAcc = FDS_CRC_INIT;
    this.crcError = false;
    this.driveCycles = 0;
    this.position = 0;
    this.inBlock = false;
    this.blockRemaining = 0;
    this.currentBlock = -1;
    this.endOfHead = false;

    // A block being written, between its start mark and the CRC transfer.
    this.writeActive = false;
    this.writeBuffer = [];
    this.writeCrc = FDS_CRC_INIT;
    this.writeTarget = -1;

    // Drive status, latched from the console options in reset() like the PAL
    // length and the port test jig, so the UI can flip them and reset.
    this.writeProtected = this.nes.opts.fdsWriteProtect === true;
    this.diskPresent = this.nes.opts.fdsDiskInserted !== false;

    // Keep the side the drive is on across a reset (a console reset does not
    // eject the disk), but start the scan from the beginning of the side.
    if (this.sides && this.sides.length > 0) {
      this.selectSide(this.sideIndex || 0);
    } else {
      this.blocks = [];
      this.stream = new Uint8Array(0);
      this.blockMarks = [];
    }
  };

  // ---- CPU memory map ------------------------------------------------

  Mappers[20].prototype.load = function (address) {
    address &= 0xffff;

    if (address >= 0xe000) {
      // BIOS ROM. The reset and interrupt vectors live at the top of it.
      return this.bios ? this.bios[address - 0xe000] : 0;
    }
    if (address >= 0x6000) {
      return this.prgRam[address - 0x6000];
    }
    if (address >= 0x4020 && address <= 0x4033) {
      return this.readRegister(address);
    }
    return Mappers[0].prototype.load.call(this, address);
  };

  Mappers[20].prototype.write = function (address, value) {
    address &= 0xffff;

    if (address >= 0x6000 && address < 0xe000) {
      this.prgRam[address - 0x6000] = value & 0xff;
      return;
    }
    if (address >= 0x4020 && address <= 0x4033) {
      this.writeRegister(address, value & 0xff);
      return;
    }
    Mappers[0].prototype.write.call(this, address, value);
  };

  // ---- $4020-$4033 ---------------------------------------------------

  Mappers[20].prototype.readRegister = function (address) {
    switch (address) {
      case 0x4030: {
        // Disk status. Reading it acknowledges both IRQs and clears the byte
        // transfer flag, which is what the BIOS' transfer loop relies on.
        var status = 0;
        if (this.diskPresent) status |= 0x80; // readable / writable
        if (this.timerIrq) status |= 0x01;
        if (this.byteReady) status |= 0x02;
        if (this.crcError) status |= 0x10;
        if (this.endOfHead) status |= 0x40;

        this.timerIrq = false;
        this.diskIrq = false;
        this.byteReady = false;
        return status;
      }

      case 0x4031:
        // Read data register. Servicing it acknowledges the disk IRQ.
        this.diskIrq = false;
        this.byteReady = false;
        return this.byteValue;

      case 0x4032: {
        // Drive status: bit 0 disk set (0 = inserted), bit 1 head at the end /
        // not ready, bit 2 write protected. Bit 6 is documented to read back 1.
        var drive = 0x40;
        if (!this.diskPresent) drive |= 0x01;
        if (this.endOfHead) drive |= 0x02;
        if (this.writeProtected) drive |= 0x04;
        return drive;
      }

      case 0x4033:
        // Expansion connector read. Open collector: a bit the adaptor holds low
        // reads back as 0, the rest are pulled high, and bit 7 is the battery
        // (power good) line the BIOS waits for in WaitForRdy.
        return 0x80 | (this.extWrite & 0x7f);

      default:
        // $4020-$402F are write-only, $4034+ unconnected: the address's high
        // byte stays on the bus.
        return 0x40;
    }
  };

  Mappers[20].prototype.writeRegister = function (address, value) {
    switch (address) {
      case 0x4020:
        this.timerReload = (this.timerReload & 0xff00) | value;
        break;

      case 0x4021:
        this.timerReload = (this.timerReload & 0x00ff) | (value << 8);
        break;

      case 0x4022: {
        // Timer IRQ control: bit 0 repeat, bit 1 enable. Gated by $4023.0.
        if (!this.diskIoEnabled) break;
        this.timerRepeat = (value & 0x01) !== 0;
        if ((value & 0x02) !== 0) {
          this.timerEnabled = true;
          this.timerCounter = this.timerReload;
          this.timerIrq = false;
        } else {
          this.timerEnabled = false;
          this.timerCounter = 0;
          this.timerIrq = false; // also acknowledges a pending timer IRQ
        }
        break;
      }

      case 0x4023:
        // Master I/O enable. Turning the disk registers off stops the timer and
        // acknowledges both IRQs.
        this.diskIoEnabled = (value & 0x01) !== 0;
        this.soundIoEnabled = (value & 0x02) !== 0;
        if (!this.diskIoEnabled) {
          this.timerEnabled = false;
          this.timerIrq = false;
          this.diskIrq = false;
          this.byteReady = false;
        }
        break;

      case 0x4024:
        if (!this.diskIoEnabled) break;
        this.diskData = value;
        this.diskIrq = false; // writing acknowledges the disk IRQ
        this.writeDiskByte(value);
        break;

      case 0x4025:
        if (!this.diskIoEnabled) break;
        this.writeDiskControl(value);
        break;

      case 0x4026:
        this.extWrite = value;
        break;

      default:
        break;
    }
  };

  Mappers[20].prototype.writeDiskControl = function (value) {
    var readMode = (value & 0x04) !== 0;
    var crcControl = (value & 0x10) !== 0;
    var transferBehaviour = (value & 0x40) !== 0;
    var irqEnabled = (value & 0x80) !== 0;

    // Bit 3: mirroring, 0 = vertical, 1 = horizontal.
    this.setMirroring(
      (value & 0x08) !== 0
        ? this.nes.rom.HORIZONTAL_MIRRORING
        : this.nes.rom.VERTICAL_MIRRORING,
    );

    this.motorOn = (value & 0x01) !== 0;

    if ((value & 0x02) !== 0) {
      // Transfer reset: the head is back at the beginning of the disk, and no
      // data is transferred while the bit is set. WaitForRdy cycles it to park
      // the head before every disk call.
      this.transferReset = true;
      this.position = 0;
      this.driveCycles = 0;
      this.inBlock = false;
      this.blockRemaining = 0;
      this.byteReady = false;
      this.crcAcc = FDS_CRC_INIT;
      this.crcError = false;
      this.endOfHead = false;
    } else {
      this.transferReset = false;
    }

    // Raising bit 6 asks the drive to resynchronise on the next block start
    // mark and resets the CRC accumulator, so stale state from the previous
    // block cannot leak into this one.
    if (transferBehaviour && !this.transferBehaviour) {
      this.byteReady = false;
      this.inBlock = false;
      this.blockRemaining = 0;
      this.crcAcc = FDS_CRC_INIT;
      this.crcError = false;
    }
    this.transferBehaviour = transferBehaviour;
    this.readMode = readMode;

    // CRC control during a write: the adaptor streams its CRC register to the
    // disk, which ends the block.
    if (crcControl && !this.crcControl && !readMode && this.writeActive) {
      this.finishWriteBlock();
    }
    this.crcControl = crcControl;

    // Writing $4025 acknowledges the disk IRQ; but if a byte is already sitting
    // in the register and interrupts are being turned on, one has to be raised
    // for it, or the BIOS' XferByte would wait forever.
    var wasEnabled = this.diskIrqEnabled;
    this.diskIrq = false;
    this.diskIrqEnabled = irqEnabled;
    if (irqEnabled && !wasEnabled && this.byteReady) {
      this.diskIrq = true;
      this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
    }

    this.diskControl = value;
  };

  // ---- the drive -----------------------------------------------------

  // Clocks the timer IRQ (one decrement per CPU cycle) and, when the drive is
  // scanning in read mode, the serial byte stream.
  Mappers[20].prototype.clockCpuCycles = function (cycles) {
    this.clockTimer(cycles);

    if (!this.diskIoEnabled || !this.motorOn || this.transferReset) return;
    if (this.transferBehaviour && this.readMode) {
      this.clockDrive(cycles);
    }
  };

  Mappers[20].prototype.clockTimer = function (cycles) {
    if (!this.timerEnabled) return;

    for (var i = 0; i < cycles; i++) {
      if (this.timerCounter !== 0) {
        this.timerCounter--;
        continue;
      }

      // The counter ran out: raise the IRQ and reload. Without the repeat flag
      // the timer stops after its first IRQ, exactly like the 2C33.
      this.timerIrq = true;
      this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
      this.timerCounter = this.timerReload;
      if (!this.timerRepeat) {
        this.timerEnabled = false;
        return;
      }
    }
  };

  Mappers[20].prototype.clockDrive = function (cycles) {
    // The byte transfer flag stays up until the CPU services $4030/$4031, so
    // the drive holds the next byte back rather than overrunning it. The BIOS'
    // consumer is a handful of cycles behind the IRQ, never 149.
    if (this.byteReady) return;

    this.driveCycles += cycles;
    while (this.driveCycles >= FDS_BYTE_CYCLES) {
      this.driveCycles -= FDS_BYTE_CYCLES;
      this.advanceRead();
      if (this.byteReady) break;
    }
  };

  Mappers[20].prototype.advanceRead = function () {
    if (!this.inBlock) {
      // Between blocks the adaptor sees nothing but the gap's zero bits; the
      // block starts at the first set bit, the mark.
      var stream = this.stream;
      var p = this.position;

      while (p < stream.length && stream[p] !== FDS_MARK) p++;

      if (p >= stream.length) {
        // End of the head: it clicks back to the start of the side. The BIOS
        // sees this as $4030 bit 6 / $4032 bit 1.
        this.endOfHead = true;
        this.position = 0;
        p = 0;
        while (p < stream.length && stream[p] !== FDS_MARK) p++;
        if (p >= stream.length) return;
      }

      this.currentBlock = this.indexAtMark(p);
      this.position = p + 1; // the mark itself is consumed here
      this.inBlock = true;
      this.crcAcc = FDS_CRC_INIT;
      this.crcError = false;

      var block = this.blocks[this.currentBlock];
      this.blockRemaining = block ? block.raw.length + 2 : 0;
      if (this.blockRemaining === 0) this.inBlock = false;
      return; // the mark takes one transfer slot, the first byte comes next
    }

    var value = this.stream[this.position++];
    this.blockRemaining--;

    // The CRC register runs over every byte as it is read, including the two
    // CRC bytes themselves. With CRC control set the adaptor is streaming its
    // own register, and that is the byte the BIOS uses to decide pass/fail.
    this.crcAcc = crcFeed(this.crcAcc, value);
    if (this.crcControl) {
      this.crcError = this.crcAcc !== 0;
    }

    if (this.blockRemaining <= 0) this.inBlock = false;

    this.deliverByte(value);
  };

  Mappers[20].prototype.deliverByte = function (value) {
    this.byteValue = value & 0xff;
    this.byteReady = true;

    if (this.diskIrqEnabled) {
      this.diskIrq = true;
      this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
    }
  };

  Mappers[20].prototype.writeDiskByte = function (value) {
    // "While [bit 6] is 0, data in $4024 is ignored": only a block being written
    // takes bytes, and only in write mode.
    if (!this.transferBehaviour || this.readMode) return;

    if (!this.writeActive) {
      // The BIOS opens a block by writing $80: the block start mark.
      if (value === FDS_MARK) {
        this.writeActive = true;
        this.writeBuffer = [];
        this.writeCrc = FDS_CRC_INIT;
        this.writeTarget = this.currentBlockIndex();
      }
      return;
    }

    this.writeBuffer.push(value);
    this.writeCrc = crcFeed(this.writeCrc, value);
  };

  // Called when the BIOS raises CRC control after the last data byte: the
  // hardware appends its CRC register to the block and the block is done.
  Mappers[20].prototype.finishWriteBlock = function () {
    if (!this.writeActive) return;

    var raw = new Uint8Array(this.writeBuffer);
    var index = this.writeTarget;
    if (index < 0 || index > this.blocks.length) index = this.blocks.length;

    if (raw.length > 0) {
      this.blocks[index] = {
        type: raw[0],
        raw: raw,
        crc: this.writeCrc & 0xffff,
      };
    }

    this.writeActive = false;
    this.writeBuffer = [];
    this.writeTarget = -1;

    this.commitSide();
    this.rebuildStream();

    if (raw.length > 0) {
      // The head is now past the block that was just written, so the next
      // WriteBlkType appends after it.
      this.position = this.blockMarks[index] + 1 + raw.length + 2;
    }
    this.inBlock = false;
    this.blockRemaining = 0;
    this.byteReady = false;
    this.endOfHead = false;
  };

  // ---- disk layout ---------------------------------------------------

  // Walks the block structure of the current side. A .fds side is exactly the
  // blocks back to back: no gaps, no CRCs, in the order 1, 2, 3, 4, 3, 4...
  Mappers[20].prototype.parseSide = function () {
    var data = this.sides[this.sideIndex];
    var blocks = [];

    if (data) {
      var offset = 0;
      var fileSize = 0;

      while (offset < data.length) {
        var type = data[offset];
        var length = 0;

        if (type === 1) {
          length = 56; // disk info: code + 53 bytes (CRC stripped)
        } else if (type === 2) {
          length = 2; // file amount: code + count
        } else if (type === 3) {
          length = 16; // file header: code + 13 fields (CRC stripped)
          if (offset + 16 > data.length) break;
          fileSize = data[offset + 0x0d] | (data[offset + 0x0e] << 8);
        } else if (type === 4) {
          length = 1 + fileSize; // file data: code + payload
        } else {
          // A gap (0), a test-data block (5, length is not recoverable without
          // the CRC), or a corrupt side: stop here and keep what parsed.
          break;
        }

        if (length < 1 || offset + length > data.length) break;

        var raw = data.subarray(offset, offset + length);
        blocks.push({
          type: type,
          raw: new Uint8Array(raw),
          crc: blockCrc(raw),
        });

        offset += length;
      }
    }

    this.blocks = blocks;
    this.rebuildStream();
  };

  // Serialises the parsed blocks back into the flat .fds side layout. Called
  // after a write, so a game that saves ends up with a consistent image.
  Mappers[20].prototype.commitSide = function () {
    var data = this.sides[this.sideIndex];
    if (!data) return;

    data.fill(0);
    var offset = 0;
    for (var i = 0; i < this.blocks.length; i++) {
      var raw = this.blocks[i].raw;
      if (offset + raw.length > data.length) break;
      data.set(raw, offset);
      offset += raw.length;
    }
  };

  // Builds what the drive actually reads: for every block, a gap of zeros, the
  // start mark, the block's bytes, and the two CRC bytes. The gaps are what the
  // mark detection skips over.
  Mappers[20].prototype.rebuildStream = function () {
    var size = FDS_GAP + FDS_TRAILING_GAP;
    var i;
    for (i = 0; i < this.blocks.length; i++) {
      size += 1 + this.blocks[i].raw.length + 2 + FDS_GAP;
    }

    var stream = new Uint8Array(size);
    var marks = [];
    var pos = FDS_GAP; // leading gap: already zeroed

    for (i = 0; i < this.blocks.length; i++) {
      var block = this.blocks[i];
      marks[i] = pos;
      stream[pos++] = FDS_MARK;
      stream.set(block.raw, pos);
      pos += block.raw.length;
      stream[pos++] = block.crc & 0xff;
      stream[pos++] = (block.crc >> 8) & 0xff;
      pos += FDS_GAP; // gap before the next block (the last one is trailing)
    }

    this.stream = stream;
    this.blockMarks = marks;
  };

  Mappers[20].prototype.indexAtMark = function (position) {
    for (var i = 0; i < this.blockMarks.length; i++) {
      if (this.blockMarks[i] === position) return i;
    }
    return -1;
  };

  // The block the head is about to reach, for a write: the next mark at or
  // after the current position, or one past the last block (append).
  Mappers[20].prototype.currentBlockIndex = function () {
    for (var i = 0; i < this.blockMarks.length; i++) {
      if (this.blockMarks[i] >= this.position) return i;
    }
    return this.blocks.length;
  };

  // ---- API used by the UI and the tools ------------------------------

  Mappers[20].prototype.setDiskSide = function (index) {
    if (!this.sides || this.sides.length === 0) return;
    this.selectSide(index);
  };

  Mappers[20].prototype.getDiskSide = function () {
    return this.sideIndex || 0;
  };

  Mappers[20].prototype.getDiskSideCount = function () {
    return this.sides ? this.sides.length : 0;
  };

  Mappers[20].prototype.getDiskInfo = function (index) {
    // Block 1 is always the first record on a side and carries the game code,
    // side number and disk number; the UI labels the side picker with them.
    // Passing an index reads that side without switching to it.
    if (!this.sides || this.sides.length === 0) return null;

    var count = this.sides.length;
    var i;
    if (index === undefined || index === null) {
      i = this.sideIndex || 0;
    } else {
      i = (((index | 0) % count) + count) % count;
    }

    var raw = this.sides[i];
    if (!raw || raw[0] !== 1) {
      return null;
    }
    return {
      gameName: String.fromCharCode(raw[0x10], raw[0x11], raw[0x12]),
      version: raw[0x14],
      side: raw[0x15],
      disk: raw[0x16],
      bootId: raw[0x19],
    };
  };

  // The whole image, in the fwNES container, with whatever the game wrote to
  // this side included. The page offers it as a download so saves survive.
  Mappers[20].prototype.exportDisk = function () {
    var count = this.sides.length;
    var out = new Uint8Array(16 + count * FDS_SIDE_SIZE);
    out[0] = 0x46;
    out[1] = 0x44;
    out[2] = 0x53;
    out[3] = 0x1a;
    out[4] = count;
    for (var i = 0; i < count; i++) {
      out.set(this.sides[i], 16 + i * FDS_SIDE_SIZE);
    }
    return out;
  };

  Mappers[20].prototype.selectSide = function (index) {
    var count = this.sides ? this.sides.length : 0;
    if (count === 0) return;

    this.sideIndex = (((index | 0) % count) + count) % count;
    this.parseSide();

    this.position = 0;
    this.driveCycles = 0;
    this.inBlock = false;
    this.blockRemaining = 0;
    this.currentBlock = -1;
    this.byteReady = false;
    this.diskIrq = false;
    this.crcAcc = FDS_CRC_INIT;
    this.crcError = false;
    this.endOfHead = false;
    this.writeActive = false;
    this.writeBuffer = [];
    this.writeTarget = -1;
  };

  // ---- power on ------------------------------------------------------

  Mappers[20].prototype.loadROM = function () {
    var rom = this.nes.rom;
    if (!rom || !rom.valid) {
      throw new Error("FDS: Invalid ROM! Unable to load.");
    }
    if (!rom.isFds) {
      // Mapper 20 exists for the RAM adaptor; a cartridge image declaring it is
      // not something this build can make sense of.
      throw new Error("FDS: mapper 20 needs a .fds disk image (and a BIOS).");
    }

    var bios = toByteArray(this.nes.opts.fdsBios);
    if (!bios || bios.length < FDS_BIOS_SIZE) {
      throw new Error(
        "FDS : BIOS manquante (disksys.rom, 8 Ko). " +
          "Chargez-la dans le panneau Jeu, ou deposez disksys.rom a cote de la page.",
      );
    }

    this.bios = new Uint8Array(FDS_BIOS_SIZE);
    this.bios.set(bios.subarray(0, FDS_BIOS_SIZE));

    this.reset();

    this.prgRam = new Uint8Array(FDS_RAM_SIZE);
    this.sides = rom.fdsSides;
    this.sideIndex = 0;
    this.selectSide(0);
    this.setMirroring(this.nes.rom.VERTICAL_MIRRORING);

    this.resetIrqHandler();
  };

  Mappers[20].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);

    s.timerReload = this.timerReload;
    s.timerCounter = this.timerCounter;
    s.timerEnabled = this.timerEnabled;
    s.timerRepeat = this.timerRepeat;
    s.timerIrq = this.timerIrq;
    s.diskIoEnabled = this.diskIoEnabled;
    s.soundIoEnabled = this.soundIoEnabled;
    s.diskData = this.diskData;
    s.diskControl = this.diskControl;
    s.extWrite = this.extWrite;
    s.motorOn = this.motorOn;
    s.transferReset = this.transferReset;
    s.readMode = this.readMode;
    s.crcControl = this.crcControl;
    s.transferBehaviour = this.transferBehaviour;
    s.diskIrqEnabled = this.diskIrqEnabled;
    s.byteReady = this.byteReady;
    s.byteValue = this.byteValue;
    s.diskIrq = this.diskIrq;
    s.crcAcc = this.crcAcc;
    s.crcError = this.crcError;
    s.driveCycles = this.driveCycles;
    s.position = this.position;
    s.inBlock = this.inBlock;
    s.blockRemaining = this.blockRemaining;
    s.currentBlock = this.currentBlock;
    s.endOfHead = this.endOfHead;
    s.writeProtected = this.writeProtected;
    s.diskPresent = this.diskPresent;
    s.sideIndex = this.sideIndex;
    // 32 KB of PRG RAM: a plain array, so the state survives a trip through
    // JSON (which is how the page saves).
    s.prgRam = Array.prototype.slice.call(this.prgRam);
    return s;
  };

  Mappers[20].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);

    this.timerReload = s.timerReload;
    this.timerCounter = s.timerCounter;
    this.timerEnabled = s.timerEnabled;
    this.timerRepeat = s.timerRepeat;
    this.timerIrq = s.timerIrq;
    this.diskIoEnabled = s.diskIoEnabled;
    this.soundIoEnabled = s.soundIoEnabled;
    this.diskData = s.diskData;
    this.diskControl = s.diskControl;
    this.extWrite = s.extWrite;
    this.motorOn = s.motorOn;
    this.transferReset = s.transferReset;
    this.readMode = s.readMode;
    this.crcControl = s.crcControl;
    this.transferBehaviour = s.transferBehaviour;
    this.diskIrqEnabled = s.diskIrqEnabled;
    this.byteReady = s.byteReady;
    this.byteValue = s.byteValue;
    this.diskIrq = s.diskIrq;
    this.crcAcc = s.crcAcc;
    this.crcError = s.crcError;
    this.driveCycles = s.driveCycles;
    this.inBlock = s.inBlock;
    this.blockRemaining = s.blockRemaining;
    this.currentBlock = s.currentBlock;
    this.endOfHead = s.endOfHead;
    this.writeProtected = s.writeProtected;
    this.diskPresent = s.diskPresent;
    this.prgRam = new Uint8Array(s.prgRam);

    // The blocks and the byte stream are derived from the side image, which
    // lives in the ROM, so they are rebuilt rather than saved.
    this.sideIndex = s.sideIndex || 0;
    this.parseSide();
    this.position = s.position;
  };
}

module.exports = { register: register };
